import {
  getVehicleQuotes,
  mapCitysprintVehicleToNetworkRows,
} from './mapCitysprintQuoteToNetworks';
import {
  CITYSPRINT_QUOTE_BUCKET_ORDER,
  classifyCitysprintQuoteBucket,
} from './citysprintQuoteBuckets';

function formatDimensionsMetres(cap) {
  if (cap == null || typeof cap !== 'object') {
    return '—';
  }
  const a = cap.maxLengthCM;
  const b = cap.maxWidthCM;
  const c = cap.maxHeightCM;
  if (a == null || b == null || c == null) {
    return '—';
  }
  const toM = x => {
    const m = x / 100;
    return Number.isInteger(m) ? String(m) : m.toFixed(1).replace(/\.0$/, '');
  };
  return `${toM(a)}m × ${toM(b)}m × ${toM(c)}m`;
}

function formatDimensionsCm(cap) {
  if (cap == null || typeof cap !== 'object') {
    return '—';
  }
  const a = cap.maxLengthCM;
  const b = cap.maxWidthCM;
  const c = cap.maxHeightCM;
  if (a == null || b == null || c == null) {
    return '—';
  }
  const fmt = x =>
    Number.isInteger(x) ? String(x) : Number(x).toFixed(1).replace(/\.0$/, '');
  return `${fmt(a)} (L) × ${fmt(b)} (W) × ${fmt(c)} (H) cm`;
}

function formatDimensionValueCm(x) {
  if (typeof x !== 'number' || !Number.isFinite(x)) {
    return '—';
  }
  if (x >= 100) {
    const metres = x / 100;
    const formatted = Number.isInteger(metres)
      ? String(metres)
      : metres.toFixed(1).replace(/\.0$/, '');
    return `${formatted}m`;
  }
  const formatted = Number.isInteger(x)
    ? String(x)
    : x.toFixed(1).replace(/\.0$/, '');
  return `${formatted}cm`;
}

function formatDimensionLinesCm(cap) {
  if (cap == null || typeof cap !== 'object') {
    return null;
  }
  const a = cap.maxLengthCM;
  const b = cap.maxWidthCM;
  const c = cap.maxHeightCM;
  if (a == null || b == null || c == null) {
    return null;
  }
  return [
    { axis: 'L', text: `L: ${formatDimensionValueCm(a)}` },
    { axis: 'W', text: `W: ${formatDimensionValueCm(b)}` },
    { axis: 'H', text: `H: ${formatDimensionValueCm(c)}` },
  ];
}

function vehicleLabelFromQuoteRow(row) {
  if (row.vehicleDescription) {
    return row.vehicleDescription;
  }
  const stripped = String(row.networkDesc || '')
    .replace(/\s*\([^)]*\)\s*$/, '')
    .trim();
  if (stripped) {
    return stripped;
  }
  return row.networkDesc || 'Vehicle';
}

function parcelCardSortPrice(card) {
  const n = Number(card?.prices?.incl);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

function bucketMetaForRow(row) {
  const key = classifyCitysprintQuoteBucket(row);
  const meta = CITYSPRINT_QUOTE_BUCKET_ORDER.find(b => b.key === key);
  return {
    id: meta?.id ?? 'same-day',
    title: meta?.title ?? 'Same Day',
    bucketKey: key,
  };
}

function serviceDisplayNameFromRow(row) {
  if (typeof row.serviceDescription === 'string') {
    const t = row.serviceDescription.trim();
    if (t) {
      return t;
    }
  }
  if (typeof row.parcelSectionTitle === 'string') {
    const t = row.parcelSectionTitle.trim();
    if (t) {
      return t;
    }
  }
  if (row.networkDesc) {
    return row.networkDesc;
  }
  return 'Service';
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function collapseWhitespace(title) {
  return String(title ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Every distinct piece of the vehicle label that might also appear in the
 * service title — full string, parentheticals, inner paren text, and words.
 * Longest first so multi-word matches win before single words.
 */
function vehicleDescriptionPieces(vehicleDescription) {
  const vehicle = String(vehicleDescription ?? '').trim();
  if (!vehicle) {
    return [];
  }

  const pieces = new Set([vehicle]);

  for (const parenthetical of vehicle.match(/\([^)]+\)/g) || []) {
    pieces.add(parenthetical);
    const inner = parenthetical.slice(1, -1).trim();
    if (inner) {
      pieces.add(inner);
    }
  }

  for (const word of vehicle
    .replace(/[()]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)) {
    pieces.add(word);
  }

  return [...pieces].sort((a, b) => b.length - a.length);
}

/**
 * Card title = serviceDescription with any text from vehicleDescription removed
 * wherever it appears (e.g. "Same Day Small Van" + "Small van" → "Same Day";
 * "Same Day SWB" / "Dedicated Transit (SWB)" + "Large van (SWB)" → "Same Day" /
 * "Dedicated Transit").
 */
export function stripVehicleDescriptionFromServiceTitle(
  serviceDescription,
  vehicleDescription
) {
  const service = String(serviceDescription ?? '').trim();
  if (!service) {
    return '';
  }

  let stripped = service;
  for (const piece of vehicleDescriptionPieces(vehicleDescription)) {
    stripped = collapseWhitespace(
      stripped.replace(new RegExp(escapeRegExp(piece), 'gi'), ' ')
    );
  }

  return stripped || service;
}

function serviceTitleFromRow(row) {
  return stripVehicleDescriptionFromServiceTitle(
    serviceDisplayNameFromRow(row),
    row.vehicleDescription
  );
}

function mapQuoteRowToParcelCard(row, serviceIndex, vehicleIndex, section) {
  const cap = row.vehicleCapacity;
  const maxKg =
    cap != null && typeof cap.maxWeightKG === 'number' ? cap.maxWeightKG : '—';

  const vehicleName = vehicleLabelFromQuoteRow(row);

  const dimMetres = formatDimensionsMetres(cap);

  const serviceName = serviceDisplayNameFromRow(row);
  const serviceTitle = serviceTitleFromRow(row);

  return {
    vehicleIndex,
    selectionKey: `${row.networkCode}-${vehicleIndex}-${serviceIndex}`,
    section: { id: section.id, title: section.title },
    serviceName,
    serviceTitle,
    serviceCode: row.serviceCode ?? row.networkCode,
    vehicleType: row.vehicleType,
    vehicleDescription: row.vehicleDescription,
    latestCollectionTime: row.latestCollectionTime,
    latestDeliveryTime: row.latestDeliveryTime,
    vehicle: {
      key: row.parcelVehicleKey || `${row.networkCode}-${vehicleIndex}`,
      name: vehicleName,
      dimensions: dimMetres,
      dimensionsCaption: `${dimMetres} (L×W×H)`,
      maxKg,
      vehicleIconUrl: row.vehicleIconUrl,
      iconScale: 1,
    },
    prices: {
      excl: Number(row.servicePrice ?? 0).toFixed(2),
      incl: Number(
        row.servicePriceWithVatRate ?? row.servicePrice ?? 0
      ).toFixed(2),
    },
  };
}

function vehicleCapacityFromVehicle(vehicle) {
  const cap = vehicle?.extendedVehicleDetails?.vehicleCapacity;
  if (cap == null || typeof cap !== 'object') {
    return null;
  }
  return cap;
}

function vehicleIconFromVehicle(vehicle) {
  const u = vehicle?.extendedVehicleDetails?.vehicleIcon;
  return typeof u === 'string' && u.trim() ? u.trim() : undefined;
}

function vehicleShellKeyFromRaw(vehicle, vehicleIndex) {
  if (typeof vehicle?.vehicleId === 'string' && vehicle.vehicleId.trim()) {
    return vehicle.vehicleId.trim();
  }
  if (typeof vehicle?.id === 'string' && vehicle.id.trim()) {
    return vehicle.id.trim();
  }
  return `veh-${vehicleIndex}`;
}

function appendVehicleQuoteToLayout(
  vehicle,
  vehicleIndex,
  vehicleCards,
  serviceCardsByVehicleIndex
) {
  if (vehicle == null || typeof vehicle !== 'object') {
    return;
  }

  const networkRows = mapCitysprintVehicleToNetworkRows(vehicle);
  if (!networkRows.length) {
    return;
  }

  const cap = vehicleCapacityFromVehicle(vehicle);
  const maxKg =
    cap != null && typeof cap.maxWeightKG === 'number' ? cap.maxWeightKG : '—';
  const dimsCm = formatDimensionsCm(cap);
  const dimensionLines = formatDimensionLinesCm(cap);
  const name =
    typeof vehicle?.vehicleDescription === 'string' &&
    vehicle.vehicleDescription.trim()
      ? vehicle.vehicleDescription.trim()
      : 'Vehicle';

  const shellKey = vehicleShellKeyFromRaw(vehicle, vehicleIndex);

  vehicleCards.push({
    vehicleIndex,
    selectionKey: `vehicle-step-${vehicleIndex}`,
    vehicle: {
      key: shellKey,
      name,
      dimensions: dimsCm,
      dimensionsCaption: `${dimsCm}`,
      ...(dimensionLines ? { dimensionLines } : {}),
      maxKg,
      vehicleIconUrl: vehicleIconFromVehicle(vehicle),
      iconScale: 1,
    },
  });

  const cards = networkRows
    .map((row, serviceIndex) => {
      const { id, title } = bucketMetaForRow(row);
      return mapQuoteRowToParcelCard(row, serviceIndex, vehicleIndex, {
        id,
        title,
      });
    })
    .sort(
      (a, b) =>
        parcelCardSortPrice(a) - parcelCardSortPrice(b) ||
        String(a.selectionKey).localeCompare(String(b.selectionKey))
    );

  serviceCardsByVehicleIndex[vehicleIndex] = cards;
}

export function mapCitysprintQuoteToParcelQuoteLayout(data) {
  const quotes = getVehicleQuotes(data);
  if (!quotes.length) {
    return { vehicleCards: [], serviceCardsByVehicleIndex: [] };
  }

  const vehicleCards = [];
  const serviceCardsByVehicleIndex = [];

  quotes.forEach((vehicle, vehicleIndex) => {
    appendVehicleQuoteToLayout(
      vehicle,
      vehicleIndex,
      vehicleCards,
      serviceCardsByVehicleIndex
    );
  });

  return { vehicleCards, serviceCardsByVehicleIndex };
}

export function flattenCitysprintParcelServiceCards(layout) {
  if (!layout?.serviceCardsByVehicleIndex?.length) {
    return [];
  }
  return layout.serviceCardsByVehicleIndex.flat();
}

export function findCheapestParcelServiceCard(layout) {
  const cards = flattenCitysprintParcelServiceCards(layout);
  if (!cards.length) {
    return null;
  }

  return cards.reduce((cheapest, card) => {
    const cheapestPrice = parcelCardSortPrice(cheapest);
    const cardPrice = parcelCardSortPrice(card);
    if (cardPrice < cheapestPrice) {
      return card;
    }
    if (cardPrice > cheapestPrice) {
      return cheapest;
    }
    return String(card.selectionKey).localeCompare(
      String(cheapest.selectionKey)
    ) < 0
      ? card
      : cheapest;
  });
}

export function findCheapestParcelServiceCardForVehicle(layout, vehicleIndex) {
  if (vehicleIndex == null || !layout?.serviceCardsByVehicleIndex) {
    return null;
  }
  const cards = layout.serviceCardsByVehicleIndex[vehicleIndex];
  if (!Array.isArray(cards) || !cards.length) {
    return null;
  }
  return findCheapestParcelServiceCard({
    serviceCardsByVehicleIndex: [cards],
  });
}
